import { defineStore } from 'pinia';
import { computed, Ref, ref, watch } from 'vue';
import { useAuthStore } from '@/stores/index';
import { IPaginationDTO, IYearDTO, IYearInfoDTO, YearDTO } from '@/dtos';
import { getCurrentYearAPI, getYearAnonAPI } from '@/services';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  createYearAPI,
  deleteYearAPI,
  getAllYearsAPI,
  getYearById,
  getYearInfoAPI,
  saveYearAPI,
} from '@/services/admin';
import { useUpdateStatusStore } from './updateStatus';

export const useYearStore = defineStore(
  'year',
  () => {
    const authStore = useAuthStore();
    const statusStore = useUpdateStatusStore();
    const queryClient = useQueryClient();
    const years = ref<IYearDTO[]>([]);
    const currentYear = computed(() => getCurrentYearQuery.data.value);
    const selectedYearId = ref<string>('');
    const selectedYear = computed(() => getSelectedYearQuery.data);
    const launch = ref<boolean>(false);
    const turnstart = ref<boolean>(false);
    const dueDate = ref<Date | undefined>(undefined);

    const getCurrentYearQuery = useQuery({
      queryKey: ['current-year'],
      queryFn: async (): Promise<IYearDTO> => {
        const ret = await getCurrentYearAPI();

        return new YearDTO(ret);
      },
      enabled: computed(() => !!authStore.isLoggedIn),
    });

    const getSelectedYearQuery = useQuery({
      queryKey: ['selected-year'],
      queryFn: async (): Promise<IYearDTO> => {
        const ret = await getYearById(selectedYearId.value);

        return new YearDTO(ret);
      },
      enabled: computed(
        () => !!authStore.isLoggedIn && !!authStore.isAdmin && selectedYearId.value != '',
      ),
    });

    const getYearsQuery = (pagination: Ref<IPaginationDTO>) => {
      return useQuery({
        queryKey: ['years', pagination.value],
        queryFn: async (): Promise<{ data: IYearDTO[]; count: number }> => {
          const ret = await getAllYearsAPI(pagination.value);
          return { data: ret.data.map((year) => new YearDTO(year)), count: ret.count };
        },
        enabled: computed(() => !!pagination.value),
      });
    };

    const getYearInfoQuery = (yearid: Ref<string>) => {
      return useQuery<IYearInfoDTO, Error>({
        queryKey: computed(() => ['year-info', yearid]),
        queryFn: async (): Promise<IYearInfoDTO> => {
          return await getYearInfoAPI(yearid.value);
        },
        enabled: computed(() => !!yearid.value),
      });
    };

    const getYearByIdQuery = (yearid: Ref<string>) => {
      return useQuery<IYearDTO, Error>({
        queryKey: computed(() => ['year', yearid]),
        queryFn: async (): Promise<IYearDTO> => {
          return await getYearById(yearid.value);
        },
        enabled: computed(() => !!yearid.value),
      });
    };

    const getYearAnonQuery = (yearid: Ref<string>) => {
      return useQuery<IYearDTO, Error>({
        queryKey: computed(() => ['year', yearid]),
        queryFn: async (): Promise<IYearDTO> => {
          return await getYearAnonAPI(yearid.value);
        },
        enabled: computed(() => !!yearid.value),
      });
    };
    // Mutations
    const create = useMutation({
      mutationFn: (year: IYearDTO) => {
        return createYearAPI(year);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['years'] });
        statusStore.setSuccessStatus('year-create', 'success:create');
      },
      onError: (err: string) => {
        statusStore.setErrorStatus(`year-create`, err);
      },
    });

    const update = useMutation({
      mutationFn: (year: IYearDTO) => {
        return saveYearAPI(year);
      },
      onSuccess: (_data, year: IYearDTO) => {
        queryClient.invalidateQueries({ queryKey: ['years'] });
        queryClient.invalidateQueries({ queryKey: ['year', year.id] });
        queryClient.invalidateQueries({ queryKey: ['year-info', year.id] });
        statusStore.setSuccessStatus(`year-${year.id}`, 'success:update');
      },
      onError: (err: string, year: IYearDTO) => {
        statusStore.setErrorStatus(`year-${year.id}`, err);
      },
    });

    const remove = useMutation({
      mutationFn: (yearId: string) => deleteYearAPI(yearId),
      onSuccess: (_data, yearId: string) => {
        queryClient.invalidateQueries({ queryKey: ['years'] });
        queryClient.invalidateQueries({ queryKey: ['year', yearId] });
        queryClient.invalidateQueries({ queryKey: ['year-info', yearId] });
        statusStore.setSuccessStatus(`year-${yearId}`, 'success:delete');
      },
      onError: (err: string, yearId: string) => {
        statusStore.setErrorStatus(`year-${yearId}`, err);
      },
    });

    const isPastTurnin = computed(() => {
      const now = new Date();
      const dueDateAdjusted = new Date(dueDate.value ? dueDate.value : now);
      dueDateAdjusted.setMinutes(dueDateAdjusted.getMinutes() + 30); // TODO SET 30 to an extension pulled from the db
      return dueDateAdjusted < now;
    });

    const turnin = computed(() => {
      const now = new Date();
      const dueDateAdjusted = new Date(dueDate.value ? dueDate.value : now);
      dueDateAdjusted.setMinutes(dueDateAdjusted.getMinutes() + 30); // TODO SET 30 to an extension pulled from the db
      return dueDateAdjusted;
    });

    // when user logs in, fetch all years
    watch(
      () => authStore.isLoggedIn,
      async (value) => {
        if (!value) {
          await logout();
        }
      },
    );

    watch(getCurrentYearQuery.data, (value) => {
      if (!selectedYearId.value && value) {
        selectedYearId.value = value.id;
      }
    });

    watch(
      () => selectedYearId.value,
      () => {
        queryClient.invalidateQueries({ queryKey: ['selected-year'] });
      },
    );

    async function logout() {
      years.value = [];
      launch.value = false;
      turnstart.value = false;
      dueDate.value = undefined;
    }

    // "exported" elements
    return {
      getYearsQuery,
      getYearByIdQuery,
      create,
      update,
      remove,
      isPastTurnin,
      years,
      selectedYearId,
      currentYear,
      launch,
      turnin,
      turnstart,
      getYearInfoQuery,
      selectedYear,
      getYearAnonQuery,
    };
  },
  {
    persist: {
      pick: ['yearsDisplayLimit', 'selectedYearId', 'currentYear'],
    },
  },
);
